import React, { useState, useEffect, useCallback } from 'react';
import {
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Phone,
  MessageSquare,
  User,
  Calendar,
  DollarSign,
  RefreshCw
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import apiService from './services/api';
import { Panel, LoadingSpinner, CallModal, SMSModal } from './components';

function RebookPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(42);
  const [minDays, setMinDays] = useState(28);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState('last_contact_asc');
  const [statusFilter, setStatusFilter] = useState('all');
  const [techFilter, setTechFilter] = useState('all');
  const [expandedClient, setExpandedClient] = useState(null);
  const [interactions, setInteractions] = useState([]);
  const pageSize = 50;

  // Call/SMS modals
  const [callModal, setCallModal] = useState(null);
  const [smsModal, setSmsModal] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, page_size: pageSize, sort, days, min_days: minDays };
      if (search) params.search = search;
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (techFilter && techFilter !== 'all') params.technician = techFilter;
      const result = await apiService.get('/amn/metrics/rebook-clients/', params);
      setData(result);
    } catch (err) {
      console.error('Error fetching rebook data:', err);
    } finally {
      setLoading(false);
    }
  }, [page, sort, days, minDays, search, statusFilter, techFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setPage(1); }, [sort, days, minDays, search, statusFilter, techFilter]);

  const toggleClient = async (personId) => {
    if (expandedClient === personId) {
      setExpandedClient(null);
      setInteractions([]);
      return;
    }
    setExpandedClient(personId);
    setInteractions([]);
    try {
      const resp = await apiService.get('/amn/interactions/', { person: personId, ordering: '-date_time', page_size: 50 });
      setInteractions(resp.results || resp || []);
    } catch { setInteractions([]); }
  };

  const formatDateTime = (dt) => {
    if (!dt) return '';
    const d = new Date(dt);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' });
  };

  const formatDate = (dt) => {
    if (!dt) return '';
    const d = new Date(dt + 'T12:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getTypeIcon = (type) => {
    if (type === 'phone_call' || type === 'call') return '📞';
    if (type === 'outgoing_call') return '📱';
    if (type === 'sms' || type === 'phone_text') return '💬';
    if (type === 'website_lead' || type === 'website_message') return '🌐';
    if (type === 'online_booking') return '📅';
    if (type?.includes('google_ads')) return '🔴';
    return '📋';
  };

  const getDaysBadge = (daysSince) => {
    if (daysSince == null) return null;
    if (daysSince <= 42) return <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-green-100 text-green-700">{daysSince}d</span>;
    if (daysSince <= 90) return <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">{daysSince}d</span>;
    return <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-red-100 text-red-700">{daysSince}d</span>;
  };

  const clients = data?.clients || [];
  const total = data?.total || 0;
  const statusCounts = data?.status_counts || {};
  const totalPages = Math.ceil(total / pageSize);

  const SortHeader = ({ field, children, align = 'left' }) => {
    const [col, dir] = sort.split('_').length > 2
      ? [sort.substring(0, sort.lastIndexOf('_')), sort.substring(sort.lastIndexOf('_') + 1)]
      : sort.split('_');
    const sortField = sort.replace(/_(?:asc|desc)$/, '');
    const sortDir = sort.endsWith('_asc') ? 'asc' : 'desc';
    const isActive = sortField === field;
    return (
      <th className={`px-2 py-1.5 text-${align} text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none`}
          onClick={() => setSort(`${field}_${isActive && sortDir === 'desc' ? 'asc' : 'desc'}`)}>
        <span className="inline-flex items-center gap-0.5">
          {children}
          {isActive && <span className="text-[10px]">{sortDir === 'asc' ? '▲' : '▼'}</span>}
        </span>
      </th>
    );
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold text-gray-900">Rebook</h1>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-3 h-3 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
            <input
              type="text" placeholder="Search name, phone..."
              value={search} onChange={e => setSearch(e.target.value)}
              className="pl-7 pr-2 py-1 text-xs border border-gray-200 rounded w-48 focus:outline-none focus:ring-1 focus:ring-red-300"
            />
          </div>
          <button onClick={fetchData} className="p-1 rounded border border-gray-200 hover:bg-gray-50">
            <RefreshCw className="w-3.5 h-3.5 text-gray-500" />
          </button>
        </div>
      </div>

      {/* Status count badges */}
      <div className="flex gap-2 text-xs">
        <span className="px-2 py-1 rounded bg-gray-100 text-gray-700 font-medium">{statusCounts.total_all || total} clients</span>
        <button onClick={() => { setMinDays(28); setDays(42); }}
          className={`px-2 py-1 rounded font-medium ${minDays === 28 && days === 42 ? 'bg-green-200 text-green-800' : 'bg-green-50 text-green-700'}`}>
          {statusCounts.due || 0} due (4-6wk)
        </button>
        <button onClick={() => { setMinDays(43); setDays(90); }}
          className={`px-2 py-1 rounded font-medium ${minDays === 43 && days === 90 ? 'bg-amber-200 text-amber-800' : 'bg-amber-50 text-amber-700'}`}>
          {statusCounts.overdue || 0} overdue (6-12wk)
        </button>
        <button onClick={() => { setMinDays(91); setDays(365); }}
          className={`px-2 py-1 rounded font-medium ${minDays === 91 && days === 365 ? 'bg-red-200 text-red-800' : 'bg-red-50 text-red-700'}`}>
          {statusCounts.at_risk || 0} at risk (3mo+)
        </button>
      </div>

      {/* Filters */}
      <Panel className="p-2 space-y-1.5">
        <div className="flex flex-wrap gap-1">
          <span className="text-[10px] text-gray-400 font-medium self-center mr-0.5">Last Visit</span>
          {[
            { v: [0, 365], l: 'All' },
            { v: [28, 42], l: '4-6 weeks' },
            { v: [43, 90], l: '6-12 weeks' },
            { v: [91, 180], l: '3-6 months' },
            { v: [181, 365], l: '6-12 months' },
          ].map(f => {
            const active = minDays === f.v[0] && days === f.v[1];
            return (
              <button key={f.l} onClick={() => { setMinDays(f.v[0]); setDays(f.v[1]); }}
                className={`px-2 py-0.5 text-[11px] font-medium rounded transition-colors ${active ? 'bg-red-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                {f.l}
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap gap-1">
          <span className="text-[10px] text-gray-400 font-medium self-center mr-0.5">Status</span>
          {[
            { v: 'all', l: 'All' },
            { v: 'none', l: 'Not contacted' },
            { v: 'contacted', l: 'Contacted' },
            { v: 'scheduled', l: 'Scheduled' },
            { v: 'excluded', l: 'Excluded' },
          ].map(s => (
            <button key={s.v} onClick={() => setStatusFilter(s.v)}
              className={`px-2 py-0.5 text-[11px] font-medium rounded transition-colors ${statusFilter === s.v ? 'bg-red-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              {s.l}
            </button>
          ))}
        </div>
      </Panel>

      {/* Table */}
      {loading ? (
        <Panel className="p-6"><LoadingSpinner /></Panel>
      ) : (
        <Panel noPadding className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <SortHeader field="name">Client</SortHeader>
                  <SortHeader field="last_appt">Last Appt</SortHeader>
                  <SortHeader field="days_since" align="center">Days Since</SortHeader>
                  <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">Last Service</th>
                  <SortHeader field="technician">Technician</SortHeader>
                  <SortHeader field="visits" align="center">Visits</SortHeader>
                  <SortHeader field="spent" align="right">Total Spent</SortHeader>
                  <SortHeader field="last_contact" align="center">Last Contact</SortHeader>
                  <th className="px-2 py-1.5 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {clients.map((c) => (
                  <React.Fragment key={c.client_id}>
                    <tr className={`hover:bg-gray-50 cursor-pointer ${expandedClient === c.person_id ? 'bg-amber-50' : ''}`}
                        onClick={() => toggleClient(c.person_id)}>
                      <td className="px-2 py-1.5">
                        <div className="flex items-center gap-1">
                          <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform ${expandedClient === c.person_id ? 'rotate-180' : ''}`} />
                          <div>
                            <a href={`/clients/${c.client_id}`} onClick={e => { e.stopPropagation(); navigate(`/clients/${c.client_id}`); }}
                              className="text-xs font-medium text-blue-700 hover:underline">{c.name || 'Unknown'}</a>
                            <p className="text-[10px] text-gray-400">{c.phone}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-1.5 text-xs text-gray-600">{formatDate(c.last_appt_date)}</td>
                      <td className="px-2 py-1.5 text-center">{getDaysBadge(c.days_since_last_appt)}</td>
                      <td className="px-2 py-1.5 text-xs text-gray-600 max-w-[150px] truncate">{c.last_services || '—'}</td>
                      <td className="px-2 py-1.5 text-xs text-gray-600">{c.last_technician_name || '—'}</td>
                      <td className="px-2 py-1.5 text-center text-xs text-gray-700 font-medium">{c.total_appts}</td>
                      <td className="px-2 py-1.5 text-right text-xs text-gray-700 font-medium">${Math.round(c.total_spent).toLocaleString()}</td>
                      <td className="px-2 py-1.5 text-center">
                        {c.last_contact_date ? (
                          <span className="text-[10px] text-gray-500" title={c.last_contact_type}>{formatDate(c.last_contact_date)}</span>
                        ) : (
                          <span className="text-[10px] text-red-400">None</span>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <div className="flex items-center justify-center gap-1" onClick={e => e.stopPropagation()}>
                          <button onClick={() => setCallModal({ personId: c.person_id, name: c.name, phone: c.phone })}
                            className="p-1 rounded hover:bg-blue-50" title="Call">
                            <Phone className="w-3 h-3 text-blue-600" />
                          </button>
                          <button onClick={() => setSmsModal({ personId: c.person_id, name: c.name, phone: c.phone })}
                            className="p-1 rounded hover:bg-green-50" title="Text">
                            <MessageSquare className="w-3 h-3 text-green-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expandedClient === c.person_id && (
                      <tr>
                        <td colSpan={9} className="px-4 py-2 bg-gray-50 border-b border-gray-200">
                          {interactions.length === 0 ? (
                            <p className="text-xs text-gray-400 py-1">Loading...</p>
                          ) : (
                            <div className="space-y-2">
                              {interactions.map(i => {
                                const isOlivia = ((i.metadata || {}).source || '').startsWith('olivia_');
                                const dir = (i.metadata || {}).direction;
                                const isOut = dir === 'outgoing' || i.type === 'outgoing_call';
                                const isIn = dir === 'incoming' || dir === 'inbound' || (!isOut && (i.type === 'phone_call' || i.type === 'call' || i.type?.includes('website') || i.type?.includes('google_ads')));
                                return (
                                  <div key={i.id} className={`flex items-start gap-2 text-xs border-l-2 pl-3 py-0.5 ${isOlivia ? 'border-purple-300 bg-purple-50/50' : isOut ? 'border-green-300' : isIn ? 'border-blue-300' : 'border-gray-200'}`}>
                                    <span className="flex-shrink-0">{getTypeIcon(i.type)}</span>
                                    <span className="text-gray-400 w-28 flex-shrink-0">{formatDateTime(i.date_time)}</span>
                                    {isOlivia ? (
                                      <span className="px-1 py-0.5 rounded text-[10px] font-medium flex-shrink-0 bg-purple-100 text-purple-700">Olivia</span>
                                    ) : (
                                      <span className={`px-1 py-0.5 rounded text-[10px] font-medium flex-shrink-0 ${isOut ? 'bg-green-50 text-green-700' : isIn ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                                        {isOut ? '↑ out' : isIn ? '↓ in' : i.type?.replace(/_/g, ' ')}
                                      </span>
                                    )}
                                    <span className="text-gray-600">{i.content || ''}</span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {clients.length === 0 && !loading && (
            <div className="p-8 text-center text-gray-500">
              <User className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="text-xs">No clients found</p>
            </div>
          )}
        </Panel>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-2 flex items-center justify-between">
          <p className="text-xs text-gray-700">
            Page {page} of {totalPages} ({clients.length} shown of {total})
          </p>
          <div className="flex gap-1">
            <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium border border-gray-200 rounded bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
              <ChevronLeft className="w-3 h-3" /> Previous
            </button>
            <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium border border-gray-200 rounded bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
              Next <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}

      {callModal && <CallModal personId={callModal.personId} personName={callModal.name} phoneNumber={callModal.phone} onClose={() => setCallModal(null)} />}
      {smsModal && <SMSModal personId={smsModal.personId} personName={smsModal.name} phoneNumber={smsModal.phone} onClose={() => setSmsModal(null)} />}
    </div>
  );
}

export default RebookPage;
